/* eslint-disable camelcase */
import powerUpAdapter from '@/store/modules/power-ups/adapters/power-up';
import { badgeAdapter } from '@/store/modules/user/adapters/user';

import { EntrySlip, EntrySlipResponse } from '@/interfaces/pick-em';

import insuranceFallbackAdapter from './insurance-fallback';
import postSubmissionOfferAdapter from './post-submission-offer';
import selectionAdapter from './selection';

const entrySlipAdapter = (entrySlip: EntrySlipResponse): EntrySlip => {
  const {
    id,
    aggregate_fee,
    aggregate_fee_components,
    badges: badgesResponse,
    boost_id,
    boost_payout,
    cancellation_expires_at,
    champion_points,
    created_at,
    current_max_payout_multiplier,
    current_max_unmodified_payout_multiplier,
    end_at,
    entry_count,
    entry_style_id,
    fee,
    free_entry,
    image_url,
    initial_max_payout_multiplier,
    initial_max_unmodified_payout_multiplier,
    insurance_fallbacks: insuranceFallbacksResponse,
    insured,
    minimum_shift_configuration,
    payout,
    payout_at,
    payout_option_id,
    payout_shifts,
    pickem_pool_entry,
    pickem_pool_style_id,
    pickem_pool_style_option_id,
    post_submission_offer,
    power_up,
    prize_max_payout,
    rebooted,
    selection_count,
    selections: selectionsResponse,
    settled_message,
    settled_payout_option_id,
    settled_pickem_pool_style_option_id,
    share_id,
    share_link,
    start_at,
    state,
    state_free_entry,
    status: entrySlipStatus,
    tax_status,
    title,
    type,
    user,
    username,
  } = entrySlip;

  const badges = badgesResponse?.map(badgeAdapter);
  const insuranceFallbacks = insuranceFallbacksResponse?.map(insuranceFallbackAdapter);
  const selections = selectionsResponse?.map(selectionAdapter);

  let adaptedPickEmPoolEntry = null;
  if (pickem_pool_entry) {
    adaptedPickEmPoolEntry = {
      id: pickem_pool_entry.id,
      payout: pickem_pool_entry.payout,
      pickemPoolId: pickem_pool_entry.pickem_pool_id,
      poolEntryCount: pickem_pool_entry.pool_entry_count,
    };
  }

  let adaptedUser = null;
  if (user) {
    adaptedUser = {
      id: entrySlip.user.id,
      profileColor: entrySlip.user.profile_color,
      profileIconUrl: entrySlip.user.profile_icon_url,
      username: entrySlip.user.username,
      badges: entrySlip.user.badges.map(badgeAdapter),
    };
  }

  let adaptedPowerUp = null;
  if (power_up) {
    adaptedPowerUp = powerUpAdapter(power_up);
  }

  const postSubmissionOffer = post_submission_offer
    ? postSubmissionOfferAdapter(post_submission_offer)
    : null;

  // we have two new statuses 'pending_pool_settlement' and 'pending_pool_assignment' but they're used for streaks,
  // which we don't have on web yet, so we're just going to treat them as 'active' for now
  // when the time comes, we'll need those new statuses to tell us that a user can open a new streak when they have an
  // existing streak in that state
  const status =
    entrySlipStatus === 'pending_pool_settlement' || entrySlipStatus === 'pending_pool_assignment'
      ? 'active'
      : entrySlipStatus;

  return {
    id,
    aggregateFee: aggregate_fee,
    aggregateFeeComponents: aggregate_fee_components,
    badges,
    boostId: boost_id,
    boostPayout: boost_payout,
    cancellationExpiresAt: cancellation_expires_at,
    championPoints: champion_points,
    createdAt: created_at,
    currentMaxPayoutMultiplier: current_max_payout_multiplier,
    currentMaxUnmodifiedPayoutMultiplier: current_max_unmodified_payout_multiplier,
    endAt: end_at,
    entryCount: entry_count,
    entryStyleId: entry_style_id,
    fee,
    freeEntry: free_entry,
    imageUrl: image_url,
    initialMaxPayoutMultiplier: initial_max_payout_multiplier,
    initialMaxUnmodifiedPayoutMultiplier: initial_max_unmodified_payout_multiplier,
    insuranceFallbacks,
    insured,
    minimumShiftConfiguration: minimum_shift_configuration,
    payout,
    payoutAt: payout_at,
    payoutOptionId: payout_option_id,
    payoutShifts: payout_shifts,
    pickemPoolEntry: adaptedPickEmPoolEntry,
    pickemPoolStyleId: pickem_pool_style_id,
    pickemPoolStyleOptionId: pickem_pool_style_option_id,
    postSubmissionOffer,
    powerUp: adaptedPowerUp,
    prizeMaxPayout: prize_max_payout,
    rebooted,
    selectionCount: selection_count,
    selections,
    settledMessage: settled_message,
    settledPayoutOptionId: settled_payout_option_id,
    settledPickemPoolStyleOptionId: settled_pickem_pool_style_option_id,
    shareId: share_id,
    shareLink: share_link,
    startAt: start_at,
    state,
    stateFreeEntry: state_free_entry,
    status,
    taxStatus: tax_status,
    title,
    type,
    user: adaptedUser,
    username,
  };
};

export default entrySlipAdapter;
